import { useEffect } from 'react';
import styled, { css } from 'styled-components';

import { flushSubmitResult } from '@r1-frontend/bp42/actions';
import { useCtxDispatch, useCtxSelector, useIncomingCallbacks } from '@r1-frontend/bp42/hooks';
import selectDataForPopup from '@r1-frontend/bp42/selectors/selectDataForPopup';
import { surveyAnalytics } from '@r1-frontend/survey/analytics';
import { useCtxSelector as useSurveySelector } from '@r1-frontend/survey/context/hooks';
import { getDialogDetails } from '@r1-frontend/survey/context/selectors';
import { useSurvey } from '@r1-frontend/survey/index';

import PopupResult from '@r1-frontend/ui-react/components/popups/popupResult';
import { Button } from '@r1-frontend/ui-kit/atoms/button';
import { SurveyBanner } from '@r1-frontend/ui-kit/molecules/Banner/Survey';
import { ThemedProperties } from '@r1-frontend/ui-kit/themes/types';

const ResultPopup = styled((props): JSX.Element | null => {
    const callbacks = useIncomingCallbacks();
    const dispatch = useCtxDispatch();

    const {
        needToShowResponseResult,
        result: {
            ok,
            message,
            status,
        },
        viewDetails: {
            result: {
                surveyName,
                successTitle,
            },
        },
    } = useCtxSelector(selectDataForPopup);

    const survey = useSurvey();

    useEffect(() => {
        if (ok) {
            survey.show({ type: 'context', name: surveyName, inPopup: false });
        }
    }, [ok, surveyName]);

    const surveyDetails = useSurveySelector(getDialogDetails);

    const handleClosePopup = () => {
        if (surveyDetails) {
            surveyAnalytics.context.closePopup();
        }

        callbacks.onCloseResultPopup();
        dispatch(flushSubmitResult());
    };

    const handleStartSurvey = () => {
        if (!surveyDetails) {
            return;
        }

        survey.markAsCompleted(surveyDetails.type, surveyDetails.name, surveyDetails.activeTo);
        surveyAnalytics.context.clickTakeSurvey();

        callbacks.onCloseResultPopup();
        dispatch(flushSubmitResult());
    };

    if (!needToShowResponseResult) {
        return null;
    }

    return (
        <PopupResult
            isOpen={ok !== undefined}
            type={status}
            title={ok ? successTitle : 'Произошла ошибка'}
            text={ok ? 'Мы свяжемся с вами в ближайшее время' : message}
            onClose={handleClosePopup}
            data-test='lazy-callback-popup-result'
            {...props}
        >
            {(surveyName && !survey.isComplete(surveyName) && surveyDetails)
                ? <div aria-label='survey-container'>
                    <SurveyBanner
                        image={surveyDetails.image}
                        title={surveyDetails.title}
                        description={surveyDetails.description}
                        buttonText={surveyDetails.buttonText}
                        linkProps={{
                            href: surveyDetails.externalLink,
                            target: '_blank',
                            rel: 'noopener',
                        }}
                        onClick={handleStartSurvey}
                    />
                </div>
                : <Button onClick={handleClosePopup}>
                    Закрыть
                </Button>
            }
        </PopupResult>
    );
})`
    ${({ theme }: ThemedProperties) => css`
        width: 100%;
        flex-direction: column;
        border-radius: ${theme.radii.pixel.xl}px;
        background: ${theme.colors.system.background};
    `};
`;

ResultPopup.displayName = 'ResultPopup';

export default ResultPopup;
